import { html, TemplateResult } from 'lit';
import { WEB_COMPONENT_EASM_ARTIFACTS_INVENTORY } from '../../../../webcomponents.constants';
import { customElement, property } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { GzGenericWidgetContent } from '../../../../genericDashboard/gzGenericWidget';
import { WidgetStateFrontEnd } from '../../../../genericDashboard/gzGenericWidget/types';
import { translateFn } from '../../../../utils/translator.helper';
import { formatLargeNumber } from '../../../../utils/formatter.utils';
import { EasmArtifactsInventoryPayload } from '@nirvana/shared/dtos/types/widgets/common';
import { createArtifactsInventoryColumns } from '../utils';
import { store } from '../../../../redux/store';
import { selectDashboardFilterValues } from '../../../../genericDashboard/redux/dashboard.filters.slice';
import { GenericDashboardsFiltersHelper } from '@nirvana/shared/dtos/types/generic-dashboards/gz-console';
import { generateFiltersForInventory } from '../../../utils/utils';
import { FilterType } from '../../genericTopData/types/types';
import { FontSizes, FontWeights } from '../../../../types/common/types';
import { FindingType } from '../../../types';
import { NavigationHelper } from '@nirvana/shared/navigation';

@customElement(WEB_COMPONENT_EASM_ARTIFACTS_INVENTORY)
export class GzEasmArtifactsInventoryWebComponent extends GzGenericWidgetContent {
  @property({
    type: WidgetStateFrontEnd,
    attribute: true,
    reflect: true,
  })
  state: WidgetStateFrontEnd;

  @property({
    type: Object,
    attribute: true,
    reflect: true,
  })
  payload: EasmArtifactsInventoryPayload;

  static get styles() {
    return styles;
  }

  protected renderLoader(): TemplateResult {
    return html` <gz-loader rows="1" columns="1"></gz-loader>`;
  }
  protected renderContent(): TemplateResult {
    const easmArtifactsInventory = this.payload?.easmArtifactsInventory;
    const columns = createArtifactsInventoryColumns(
      [
        {
          firstLabel: translateFn('ASN reports'),
          firstValue: this.checkIfValueExists(easmArtifactsInventory?.asnReports?.total),
          firstInventorySmartViewId: easmArtifactsInventory?.asnReports?.inventorySmartViewId as string,
          firstFilterType: FilterType.NONE,
          firstFilterDataArray: [],
          iconName: 'icon-asn-report',
        },
        {
          firstLabel: translateFn('Certificates'),
          firstValue: this.checkIfValueExists(easmArtifactsInventory?.certificateReports?.total),
          firstInventorySmartViewId: easmArtifactsInventory?.certificateReports?.inventorySmartViewId as string,
          firstFilterType: FilterType.FINDING_TYPE,
          firstFilterDataArray: [FindingType.CERTIFICATE_REPORT],
          iconName: 'icon-certificate-report',
        },
        {
          firstLabel: translateFn('IP blocks'),
          firstValue: this.checkIfValueExists(easmArtifactsInventory?.ipBlocks?.total),
          firstInventorySmartViewId: easmArtifactsInventory?.ipBlocks?.inventorySmartViewId as string,
          firstFilterType: FilterType.FINDING_TYPE,
          firstFilterDataArray: [FindingType.IP_BLOCK],
          iconName: 'icon-ip-block',
        },
        {
          firstLabel: translateFn('DNS records'),
          firstValue: this.checkIfValueExists(easmArtifactsInventory?.dnsRecords?.total),
          firstInventorySmartViewId: easmArtifactsInventory?.dnsRecords?.inventorySmartViewId as string,
          firstFilterType: FilterType.NONE,
          firstFilterDataArray: [],
          iconName: 'icon-dns-record',
        },
        {
          firstLabel: translateFn('Services'),
          firstValue: this.checkIfValueExists(easmArtifactsInventory?.services?.total),
          firstInventorySmartViewId: easmArtifactsInventory?.services?.inventorySmartViewId as string,
          firstFilterType: FilterType.NONE,
          firstFilterDataArray: [],
          iconName: 'icon-service',
        },
      ],
      this.navigateToInventory.bind(this)
    );
    return html`
      <div id="easm-dashboard-inventory-container">
        <div
          id="container-title"
          class="${this.state === WidgetStateFrontEnd.IS_ACTIVE ? 'clickable' : ''}"
          @click="${async () =>
            await this.navigateToInventory(
              this.payload?.easmArtifactsInventory?.allArtifactsSmartViewId,
              FilterType.NONE,
              ''
            )}"
        >
          <gz-text fontSize="${FontSizes.HEADING_5}" fontWeight="${FontWeights.NORMAL}">
            ${translateFn('Artifacts')}
          </gz-text>
        </div>
        <div class="containers-wrapper">${columns}</div>
      </div>
    `;
  }

  protected async navigateToInventory(
    inventorySmartViewId: string,
    filterType: FilterType,
    filterData: string,
    filterDataArray: Array<FindingType> = []
  ): Promise<void> {
    if (this.state !== WidgetStateFrontEnd.IS_ACTIVE) {
      return;
    }
    const dashboardAppState = store.getState().dashboardsApp;
    const filters = selectDashboardFilterValues(dashboardAppState);
    const companyId = GenericDashboardsFiltersHelper.getCompanyIdFromSingleSelectCompanyFilter(filters) as string;

    const filtersToSend = generateFiltersForInventory(filterType, filterData, companyId, filterDataArray);

    await NavigationHelper.navigateToEASMArtifactsInventory({
      smartViewId: inventorySmartViewId,
      filters: filtersToSend,
    });
  }

  private checkIfValueExists(value: number): string {
    const emptyString = '--';
    return this.state === WidgetStateFrontEnd.IS_ACTIVE ? formatLargeNumber(value) : emptyString;
  }
}
